import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ChipGroup from "@/components/funding/Chips";
import {
  fundingNeeds,
  projectStageOptions,
  mainActivityOptions,
  landPositionOptions,
  preferredNextStepOptions,
  funderPathway,
} from "@/content/funding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const initialFundingEnquiry = {
  kind: "project",
  name: "",
  email: "",
  organisation: "",
  location: "",
  message: "",
  privacy_consent: false,
  website: "", // honeypot
  topic: "",
  // project pathway
  project_stage: "",
  main_activity: "",
  land_position: "",
  funding_needs: [],
  funding_requirement: "",
  existing_partners: "",
  // funder pathway
  areas_of_interest: [],
  contribution_types: [],
  geographic_focus: "",
  contribution_range: "",
  funding_conditions: "",
  preferred_next_step: "",
};

const inputClasses =
  "rounded-sm border-charcoal/30 bg-cream focus-visible:ring-ochre text-charcoal placeholder:text-charcoal/40";

const Field = ({ label, required, optional, error, errorId, testId, children, htmlFor }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-sm text-charcoal">
      {label}{" "}
      {required && <span className="text-ochre-dark" aria-hidden="true">*</span>}
      {optional && <span className="text-charcoal/50">(optional)</span>}
    </Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p id={errorId} role="alert" className="mt-2 text-sm text-destructive" data-testid={testId}>
        {error}
      </p>
    )}
  </div>
);

const FundingForm = ({ enquiry, updateField, toggleListItem }) => {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const update = (field, value) => {
    updateField(field, value);
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!enquiry.name.trim()) next.name = "Please enter your name.";
    if (!enquiry.email.trim()) next.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enquiry.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!enquiry.location.trim()) next.location = "Please enter your location or region.";
    if (enquiry.message.trim().length < 10)
      next.message = "Please tell us a little more (at least 10 characters).";
    if (!enquiry.privacy_consent)
      next.privacy_consent = "Please confirm you have read the privacy note.";
    if (enquiry.kind === "project") {
      if (!enquiry.project_stage) next.project_stage = "Please choose your project stage.";
      if (!enquiry.main_activity) next.main_activity = "Please choose the main activity.";
      if (!enquiry.land_position) next.land_position = "Please choose your land position.";
      if (!enquiry.funding_needs.length)
        next.funding_needs = "Please select at least one thing that needs funding.";
    } else {
      if (!enquiry.areas_of_interest.length)
        next.areas_of_interest = "Please select at least one area of interest.";
      if (!enquiry.contribution_types.length)
        next.contribution_types = "Please select at least one type of contribution.";
      if (!enquiry.geographic_focus.trim())
        next.geographic_focus = "Please tell us your geographic focus.";
      if (!enquiry.preferred_next_step)
        next.preferred_next_step = "Please choose a preferred next step.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        kind: enquiry.kind,
        name: enquiry.name.trim(),
        email: enquiry.email.trim(),
        organisation: enquiry.organisation.trim() || null,
        location: enquiry.location.trim(),
        message: enquiry.message.trim(),
        privacy_consent: enquiry.privacy_consent,
        website: enquiry.website,
        topic: enquiry.topic || null,
        project_stage: enquiry.project_stage || null,
        main_activity: enquiry.main_activity || null,
        land_position: enquiry.land_position || null,
        funding_needs: enquiry.funding_needs.length ? enquiry.funding_needs : null,
        funding_requirement: enquiry.funding_requirement.trim() || null,
        existing_partners: enquiry.existing_partners.trim() || null,
        areas_of_interest: enquiry.areas_of_interest.length ? enquiry.areas_of_interest : null,
        contribution_types: enquiry.contribution_types.length ? enquiry.contribution_types : null,
        geographic_focus: enquiry.geographic_focus.trim() || null,
        contribution_range: enquiry.contribution_range.trim() || null,
        funding_conditions: enquiry.funding_conditions.trim() || null,
        preferred_next_step: enquiry.preferred_next_step || null,
      };
      const { data } = await axios.post(`${API}/funding-enquiries`, payload);
      if (data.email_delivery === "sent") {
        toast.success("Thank you — your enquiry is on its way.");
        setStatusMessage({
          type: "success",
          text: "Thank you for getting in touch. Your enquiry has been received.",
        });
      } else {
        toast.warning("Your enquiry was recorded, with a note below.");
        setStatusMessage({
          type: "warning",
          text: "Your enquiry has been recorded securely. There is currently a problem with email delivery on this site, so there may be a delay before it is seen — please accept our apologies, and feel free to try again shortly.",
        });
      }
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const detail = Array.isArray(raw)
        ? raw[0]?.msg
        : raw || "Something went wrong sending your enquiry. Please check the details and try again.";
      toast.error("Your enquiry could not be sent.");
      setStatusMessage({ type: "error", text: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const isProject = enquiry.kind === "project";

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="funding-enquiry-form"
      className="border border-charcoal/15 bg-cream p-7 md:p-10 space-y-6"
    >
      {/* Honeypot: hidden from users, visible to bots */}
      <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="funding-website">Website</label>
        <input
          id="funding-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={enquiry.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>

      {/* Pathway toggle */}
      <div role="group" aria-label="Enquiry type" className="flex flex-wrap gap-3" data-testid="funding-kind-toggle">
        {[
          { value: "project", label: "Project enquiry" },
          { value: "funder", label: "Funder enquiry" },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={enquiry.kind === opt.value}
            data-testid={`funding-kind-${opt.value}`}
            onClick={() => update("kind", opt.value)}
            className={`px-5 py-2.5 rounded-sm border text-sm transition-colors duration-300 ${
              enquiry.kind === opt.value
                ? "bg-eucalyptus text-cream border-eucalyptus"
                : "bg-cream text-charcoal border-charcoal/25 hover:border-ochre hover:text-ochre-dark"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {enquiry.topic && (
        <p className="text-sm text-charcoal/80 border-l-2 border-ochre pl-4" data-testid="funding-topic-note">
          Enquiry topic: <span className="font-medium">{enquiry.topic}</span>
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Name" required error={errors.name} errorId="funding-name-error" testId="funding-name-error" htmlFor="funding-name">
          <Input
            id="funding-name"
            data-testid="funding-name-input"
            className={inputClasses}
            value={enquiry.name}
            onChange={(e) => update("name", e.target.value)}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "funding-name-error" : undefined}
            autoComplete="name"
          />
        </Field>
        <Field label="Email" required error={errors.email} errorId="funding-email-error" testId="funding-email-error" htmlFor="funding-email">
          <Input
            id="funding-email"
            type="email"
            data-testid="funding-email-input"
            className={inputClasses}
            value={enquiry.email}
            onChange={(e) => update("email", e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "funding-email-error" : undefined}
            autoComplete="email"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Organisation" optional htmlFor="funding-organisation">
          <Input
            id="funding-organisation"
            data-testid="funding-organisation-input"
            className={inputClasses}
            value={enquiry.organisation}
            onChange={(e) => update("organisation", e.target.value)}
            autoComplete="organization"
          />
        </Field>
        <Field label="Location or region" required error={errors.location} errorId="funding-location-error" testId="funding-location-error" htmlFor="funding-location">
          <Input
            id="funding-location"
            data-testid="funding-location-input"
            className={inputClasses}
            value={enquiry.location}
            onChange={(e) => update("location", e.target.value)}
            aria-invalid={!!errors.location}
            aria-describedby={errors.location ? "funding-location-error" : undefined}
          />
        </Field>
      </div>

      {isProject ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Project stage" required error={errors.project_stage} errorId="funding-stage-error" testId="funding-stage-error" htmlFor="funding-stage">
              <Select value={enquiry.project_stage} onValueChange={(v) => update("project_stage", v)}>
                <SelectTrigger
                  id="funding-stage"
                  data-testid="funding-stage-select"
                  className={inputClasses}
                  aria-invalid={!!errors.project_stage}
                  aria-describedby={errors.project_stage ? "funding-stage-error" : undefined}
                >
                  <SelectValue placeholder="Select a stage" />
                </SelectTrigger>
                <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                  {projectStageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`funding-stage-option-${opt.value}`} className="focus:bg-sage">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Main activity" required error={errors.main_activity} errorId="funding-activity-error" testId="funding-activity-error" htmlFor="funding-activity">
              <Select value={enquiry.main_activity} onValueChange={(v) => update("main_activity", v)}>
                <SelectTrigger
                  id="funding-activity"
                  data-testid="funding-activity-select"
                  className={inputClasses}
                  aria-invalid={!!errors.main_activity}
                  aria-describedby={errors.main_activity ? "funding-activity-error" : undefined}
                >
                  <SelectValue placeholder="Select the main activity" />
                </SelectTrigger>
                <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                  {mainActivityOptions.map((opt) => (
                    <SelectItem key={opt} value={opt} data-testid={`funding-activity-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="focus:bg-sage">
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Land position" required error={errors.land_position} errorId="funding-land-error" testId="funding-land-error" htmlFor="funding-land">
            <Select value={enquiry.land_position} onValueChange={(v) => update("land_position", v)}>
              <SelectTrigger
                id="funding-land"
                data-testid="funding-land-select"
                className={inputClasses}
                aria-invalid={!!errors.land_position}
                aria-describedby={errors.land_position ? "funding-land-error" : undefined}
              >
                <SelectValue placeholder="exploring / negotiating / leased / owned / other" />
              </SelectTrigger>
              <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                {landPositionOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`funding-land-option-${opt.value}`} className="focus:bg-sage">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="What needs funding?" required error={errors.funding_needs} errorId="funding-needs-error" testId="funding-needs-error" htmlFor="funding-needs">
            <ChipGroup
              options={fundingNeeds.cards.map((c) => c.title)}
              selected={enquiry.funding_needs}
              onToggle={(item) => toggleListItem("funding_needs", item)}
              testIdPrefix="funding-need"
              ariaLabel="What needs funding"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Indicative funding requirement" optional htmlFor="funding-requirement">
              <Input
                id="funding-requirement"
                data-testid="funding-requirement-input"
                className={inputClasses}
                placeholder="e.g. a rough figure or range, if known"
                value={enquiry.funding_requirement}
                onChange={(e) => update("funding_requirement", e.target.value)}
              />
            </Field>
            <Field label="Existing partners or support" optional htmlFor="funding-partners">
              <Input
                id="funding-partners"
                data-testid="funding-partners-input"
                className={inputClasses}
                value={enquiry.existing_partners}
                onChange={(e) => update("existing_partners", e.target.value)}
              />
            </Field>
          </div>
        </>
      ) : (
        <>
          <Field label="Areas of interest" required error={errors.areas_of_interest} errorId="funding-interests-error" testId="funding-interests-error" htmlFor="funding-interests">
            <ChipGroup
              options={funderPathway.interests}
              selected={enquiry.areas_of_interest}
              onToggle={(item) => toggleListItem("areas_of_interest", item)}
              testIdPrefix="funding-interest"
              ariaLabel="Areas of interest"
            />
          </Field>

          <Field label="Type of contribution" required error={errors.contribution_types} errorId="funding-contributions-error" testId="funding-contributions-error" htmlFor="funding-contributions">
            <ChipGroup
              options={funderPathway.contributions}
              selected={enquiry.contribution_types}
              onToggle={(item) => toggleListItem("contribution_types", item)}
              testIdPrefix="funding-contribution-type"
              ariaLabel="Types of contribution"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Geographic focus" required error={errors.geographic_focus} errorId="funding-geo-error" testId="funding-geo-error" htmlFor="funding-geo">
              <Input
                id="funding-geo"
                data-testid="funding-geo-input"
                className={inputClasses}
                placeholder="e.g. a region, a country, or worldwide"
                value={enquiry.geographic_focus}
                onChange={(e) => update("geographic_focus", e.target.value)}
                aria-invalid={!!errors.geographic_focus}
                aria-describedby={errors.geographic_focus ? "funding-geo-error" : undefined}
              />
            </Field>
            <Field label="Indicative contribution range" optional htmlFor="funding-range">
              <Input
                id="funding-range"
                data-testid="funding-range-input"
                className={inputClasses}
                value={enquiry.contribution_range}
                onChange={(e) => update("contribution_range", e.target.value)}
              />
            </Field>
          </div>

          <Field label="Eligibility requirements or funding conditions" optional htmlFor="funding-conditions">
            <Input
              id="funding-conditions"
              data-testid="funding-conditions-input"
              className={inputClasses}
              value={enquiry.funding_conditions}
              onChange={(e) => update("funding_conditions", e.target.value)}
            />
          </Field>

          <Field label="Preferred next step" required error={errors.preferred_next_step} errorId="funding-nextstep-error" testId="funding-nextstep-error" htmlFor="funding-nextstep">
            <Select value={enquiry.preferred_next_step} onValueChange={(v) => update("preferred_next_step", v)}>
              <SelectTrigger
                id="funding-nextstep"
                data-testid="funding-nextstep-select"
                className={inputClasses}
                aria-invalid={!!errors.preferred_next_step}
                aria-describedby={errors.preferred_next_step ? "funding-nextstep-error" : undefined}
              >
                <SelectValue placeholder="Select a next step" />
              </SelectTrigger>
              <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                {preferredNextStepOptions.map((opt) => (
                  <SelectItem key={opt} value={opt} data-testid={`funding-nextstep-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="focus:bg-sage">
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </>
      )}

      <Field label="Brief message" required error={errors.message} errorId="funding-message-error" testId="funding-message-error" htmlFor="funding-message">
        <Textarea
          id="funding-message"
          rows={5}
          data-testid="funding-message-input"
          className={inputClasses}
          value={enquiry.message}
          onChange={(e) => update("message", e.target.value)}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "funding-message-error" : undefined}
        />
      </Field>

      <div className="flex items-start gap-3">
        <Checkbox
          id="funding-privacy"
          data-testid="funding-privacy-checkbox"
          className="mt-0.5 rounded-sm border-charcoal/40 data-[state=checked]:bg-eucalyptus data-[state=checked]:border-eucalyptus"
          checked={enquiry.privacy_consent}
          onCheckedChange={(checked) => update("privacy_consent", checked === true)}
          aria-invalid={!!errors.privacy_consent}
          aria-describedby={errors.privacy_consent ? "funding-privacy-error" : undefined}
        />
        <div>
          <Label htmlFor="funding-privacy" className="text-sm leading-relaxed text-charcoal/85 font-normal cursor-pointer">
            I have read the{" "}
            <Link to="/privacy" className="underline decoration-ochre underline-offset-4 text-ochre-dark hover:text-charcoal transition-colors duration-300">
              privacy note
            </Link>{" "}
            and consent to my details being used to respond to this enquiry.{" "}
            <span className="text-ochre-dark" aria-hidden="true">*</span>
          </Label>
          {errors.privacy_consent && (
            <p id="funding-privacy-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="funding-privacy-error">
              {errors.privacy_consent}
            </p>
          )}
        </div>
      </div>

      {statusMessage && (
        <div
          role="status"
          aria-live="polite"
          data-testid={`funding-status-${statusMessage.type}`}
          className={`border p-4 text-sm leading-relaxed rounded-sm ${
            statusMessage.type === "success"
              ? "border-eucalyptus/40 bg-eucalyptus/5 text-eucalyptus-deep"
              : statusMessage.type === "warning"
                ? "border-ochre/50 bg-ochre/10 text-ochre-dark"
                : "border-destructive/40 bg-destructive/5 text-destructive"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="funding-submit-button"
        className="bg-eucalyptus text-cream px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
};

export default FundingForm;
